import {
  Address,
  Package,
  WorkoutMovement,
  WorkoutMovementWrite,
  WorkoutResultCreate,
  WorkoutSectionMovementTemplate,
} from './generated';

export type Country = Address['country'];

// drf-spectacular emits choice values inline per model rather than as a shared enum,
// so we declare ScoreTypeEnum here. The drift check below keeps it aligned with the generated union.
export enum ScoreTypeEnum {
  CALORIES = 'calories',
  DISTANCE = 'distance',
  REPS = 'reps',
  ROUNDS = 'rounds',
  ROUNDS_AND_REPS = 'rounds_and_reps',
  TIME_HIGHER_BETTER = 'time_higher_better',
  TIME_LOWER_BETTER = 'time_lower_better',
  WEIGHT_SETS_REPS = 'weight_sets_reps',
}

export const SCORE_TYPE_LABEL_MAP: Record<ScoreTypeEnum, string> = {
  [ScoreTypeEnum.CALORIES]: 'Calories',
  [ScoreTypeEnum.DISTANCE]: 'Distance',
  [ScoreTypeEnum.REPS]: 'Reps',
  [ScoreTypeEnum.ROUNDS]: 'Rounds',
  [ScoreTypeEnum.ROUNDS_AND_REPS]: 'Rounds + Reps',
  [ScoreTypeEnum.TIME_HIGHER_BETTER]: 'Time (higher is better)',
  [ScoreTypeEnum.TIME_LOWER_BETTER]: 'Time (lower is better)',
  [ScoreTypeEnum.WEIGHT_SETS_REPS]: 'Weight (sets and reps)',
};

export const SCORE_TYPE_OPTIONS = Object.values(ScoreTypeEnum).map(value => ({
  value,
  label: SCORE_TYPE_LABEL_MAP[value],
}));

type GeneratedScoreType = Exclude<NonNullable<WorkoutMovementWrite['defaultScoreType']>, ''>;
type ScoreTypeEnumDriftCheck = `${ScoreTypeEnum}` extends GeneratedScoreType
  ? GeneratedScoreType extends `${ScoreTypeEnum}`
    ? true
    : never
  : never;
const scoreTypeEnumDriftCheck: ScoreTypeEnumDriftCheck = true;
void scoreTypeEnumDriftCheck;

export enum DistanceUnitEnum {
  METERS = 'm',
  KILOMETERS = 'km',
  MILES = 'mi',
}

export const DISTANCE_UNIT_LABEL_MAP: Record<DistanceUnitEnum, string> = {
  [DistanceUnitEnum.METERS]: 'meters',
  [DistanceUnitEnum.KILOMETERS]: 'kilometers',
  [DistanceUnitEnum.MILES]: 'miles',
};

export const DISTANCE_UNIT_OPTIONS = Object.values(DistanceUnitEnum).map(value => ({
  value,
  label: DISTANCE_UNIT_LABEL_MAP[value],
}));

type GeneratedDistanceUnit = Exclude<NonNullable<WorkoutSectionMovementTemplate['distanceUnit']>, ''>;
type DistanceUnitEnumDriftCheck = `${DistanceUnitEnum}` extends GeneratedDistanceUnit
  ? GeneratedDistanceUnit extends `${DistanceUnitEnum}`
    ? true
    : never
  : never;
const distanceUnitEnumDriftCheck: DistanceUnitEnumDriftCheck = true;
void distanceUnitEnumDriftCheck;

export enum WeightUnitEnum {
  KG = 'kg',
  LB = 'lb',
}

export const WEIGHT_UNIT_LABEL_MAP: Record<WeightUnitEnum, string> = {
  [WeightUnitEnum.KG]: 'kg',
  [WeightUnitEnum.LB]: 'lbs',
};

export const WEIGHT_UNIT_OPTIONS = Object.values(WeightUnitEnum).map(value => ({
  value,
  label: WEIGHT_UNIT_LABEL_MAP[value],
}));

type GeneratedWeightUnit = Exclude<NonNullable<WorkoutSectionMovementTemplate['weightUnit']>, ''>;
type WeightUnitEnumDriftCheck = `${WeightUnitEnum}` extends GeneratedWeightUnit
  ? GeneratedWeightUnit extends `${WeightUnitEnum}`
    ? true
    : never
  : never;
const weightUnitEnumDriftCheck: WeightUnitEnumDriftCheck = true;
void weightUnitEnumDriftCheck;

export const SCORE_TYPES_REQUIRING_DISTANCE_UNIT: ReadonlySet<ScoreTypeEnum> = new Set([
  ScoreTypeEnum.DISTANCE,
]);

export const SCORE_TYPES_REQUIRING_WEIGHT_UNIT: ReadonlySet<ScoreTypeEnum> = new Set([
  ScoreTypeEnum.WEIGHT_SETS_REPS,
]);

// Same drift-check approach as ScoreTypeEnum above — drf-spectacular emits choice
// values inline rather than as a shared enum.
export enum WorkoutLevelEnum {
  SCALED = 'scaled',
  RX = 'rx',
  RX_PLUS = 'rx_plus',
}

export const WORKOUT_LEVEL_LABEL_MAP: Record<WorkoutLevelEnum, string> = {
  [WorkoutLevelEnum.SCALED]: 'Scaled',
  [WorkoutLevelEnum.RX]: 'RX',
  [WorkoutLevelEnum.RX_PLUS]: 'RX+',
};

type GeneratedWorkoutLevel = Exclude<NonNullable<WorkoutResultCreate['level']>, ''>;
type WorkoutLevelEnumDriftCheck = `${WorkoutLevelEnum}` extends GeneratedWorkoutLevel
  ? GeneratedWorkoutLevel extends `${WorkoutLevelEnum}`
    ? true
    : never
  : never;
const workoutLevelEnumDriftCheck: WorkoutLevelEnumDriftCheck = true;
void workoutLevelEnumDriftCheck;

// Drift check anchored on WorkoutMovement: WorkoutHistoryRow types this field as a bare
// string, so it carries no union to check against.
export enum MovementTypeEnum {
  MOVEMENT = 'movement',
  BENCHMARK = 'benchmark',
  SCORED_SECTION = 'scored_section',
}

export const MOVEMENT_TYPE_LABEL_MAP: Record<MovementTypeEnum, string> = {
  [MovementTypeEnum.MOVEMENT]: 'Movement',
  [MovementTypeEnum.BENCHMARK]: 'Benchmark',
  [MovementTypeEnum.SCORED_SECTION]: 'Scored Section',
};

type GeneratedMovementType = Exclude<NonNullable<WorkoutMovement['movementType']>, ''>;
type MovementTypeEnumDriftCheck = `${MovementTypeEnum}` extends GeneratedMovementType
  ? GeneratedMovementType extends `${MovementTypeEnum}`
    ? true
    : never
  : never;
const movementTypeEnumDriftCheck: MovementTypeEnumDriftCheck = true;
void movementTypeEnumDriftCheck;

// The history endpoint's type filter accepts the movement types plus "all".
export const MOVEMENT_TYPE_FILTER_ALL = 'all';

export const MOVEMENT_TYPE_FILTER_OPTIONS = [
  { value: MOVEMENT_TYPE_FILTER_ALL, label: 'All' },
  ...Object.values(MovementTypeEnum).map(value => ({
    value: value as string,
    label: MOVEMENT_TYPE_LABEL_MAP[value],
  })),
];

export enum PackageTypeShortLabelsEnum {
  CREDIT_BASED = 'Credit',
  TIME_BASED = 'Time',
  TIME_OR_CREDIT_BASED = 'Time or Credit',
}
export enum PackageTypeLabelsEnum {
  CREDIT_BASED = 'Credit-based',
  TIME_BASED = 'Time-based',
  TIME_OR_CREDIT_BASED = 'Time or Credit-based',
}

type GeneratedPackageType = Package['type'];
type PackageTypeEnumDriftCheck<E> = keyof E extends GeneratedPackageType
  ? GeneratedPackageType extends keyof E
    ? true
    : never
  : never;
const packageTypeShortLabelsEnumDriftCheck: PackageTypeEnumDriftCheck<typeof PackageTypeShortLabelsEnum> =
  true;
void packageTypeShortLabelsEnumDriftCheck;
const packageTypeLabelsEnumDriftCheck: PackageTypeEnumDriftCheck<typeof PackageTypeLabelsEnum> = true;
void packageTypeLabelsEnumDriftCheck;

export enum CountryEnum {
  UK = 'GB',
  USA = 'US',
  CANADA = 'CA',
}

export const isValidCountry = (value: string | unknown): value is CountryEnum => {
  if (typeof value === 'string') {
    return Object.values(CountryEnum).includes(value as CountryEnum);
  }
  return false;
};

export type TimePeriod = TimeUnitEnum.MINUTE | TimeUnitEnum.HOUR | TimeUnitEnum.DAY;

export enum TimeUnitEnum {
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
}

export enum DayOfWeekEnum {
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
  SUNDAY = 7,
}

export const DAY_OF_WEEK_VALUES = [
  DayOfWeekEnum.MONDAY,
  DayOfWeekEnum.TUESDAY,
  DayOfWeekEnum.WEDNESDAY,
  DayOfWeekEnum.THURSDAY,
  DayOfWeekEnum.FRIDAY,
  DayOfWeekEnum.SATURDAY,
  DayOfWeekEnum.SUNDAY,
];

export const DAY_OF_WEEK_TITLE_MAP = {
  [DayOfWeekEnum.MONDAY]: 'Monday',
  [DayOfWeekEnum.TUESDAY]: 'Tuesday',
  [DayOfWeekEnum.WEDNESDAY]: 'Wednesday',
  [DayOfWeekEnum.THURSDAY]: 'Thursday',
  [DayOfWeekEnum.FRIDAY]: 'Friday',
  [DayOfWeekEnum.SATURDAY]: 'Saturday',
  [DayOfWeekEnum.SUNDAY]: 'Sunday',
};

enum BorderSize {
  xs = 0,
  sm = 1,
  md = 2,
  lg = 3,
  xl = 4,
}

export default BorderSize;

export enum NonStripePaymentMethod {
  CASH_OR_CHECK = 'Cash or Check',
  GIFT_CARD = 'Gift Card',
  E_TRANSFER = 'E Transfer',
  POS_TERMINAL = 'POS Terminal',
}

export const TAP_TO_PAY_VALUE = 'Tap to Pay';
